<template>
  <woot-modal :show="show" :on-close="onClose">
    <woot-modal-header
      :header-title="$t('BUSINESS_DATA.CATEGORY.ADD_NEW')"
    />
    <form
      class="flex flex-col modal-content"
      @submit.prevent="onSubmit"
    >
      <div class="space-y-5">
          <div class="space-y-2">
            <FormLabel
              :label="$t('BUSINESS_DATA.CATEGORY.TYPE')"
              :required="true"
              :has-error="v$.formData.category_type.$error"
            />
            <select
              v-model="formData.category_type"
              required
              :class="[
                'w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 dark:bg-slate-800 dark:text-slate-100',
                v$.formData.category_type.$error
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 dark:border-red-400 dark:focus:border-red-400'
                  : 'border-slate-200 focus:border-hichat-500 focus:ring-hichat-500/20 dark:border-slate-600'
              ]"
              @blur="v$.formData.category_type.$touch"
              @change="v$.formData.category_type.$touch"
            >
              <option value="">{{ $t('BUSINESS_DATA.CATEGORY.SELECT_TYPE') }}</option>
              <option value="faq">{{ $t('BUSINESS_DATA.CATEGORY.FAQ') }}</option>
              <option value="data">{{ $t('BUSINESS_DATA.CATEGORY.DATA') }}</option>
              <option value="image">{{ $t('BUSINESS_DATA.CATEGORY.IMAGE') }}</option>
            </select>
            <div v-if="v$.formData.category_type.$error" class="text-xs text-red-600 dark:text-red-400">
              {{ $t('BUSINESS_DATA.CATEGORY.TYPE_ERROR') }}
            </div>
          </div>

          <div class="space-y-2">
            <woot-input
              v-model="formData.name"
              :label="$t('BUSINESS_DATA.CATEGORY.NAME')"
              :required="true"
              :placeholder="$t('BUSINESS_DATA.CATEGORY.NAME_PLACEHOLDER')"
              :error="v$.formData.name.$error ? $t('BUSINESS_DATA.CATEGORY.NAME_ERROR') : ''"
              :class="{ error: v$.formData.name.$error }"
              maxlength="100"
              @blur="v$.formData.name.$touch"
            />
            <div v-if="!v$.formData.name.$error" class="flex justify-end text-xs">
              <span :class="formData.name.length > 80 ? 'text-red-500 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'">
                {{ formData.name.length }}/100
              </span>
            </div>
          </div>

          <div class="space-y-2">
            <FormLabel
              :label="$t('BUSINESS_DATA.CATEGORY.COLOR')"
              :required="true"
              :has-error="false"
            />
            <div class="flex gap-2">
              <input
                v-model="formData.color"
                type="color"
                class="h-10 w-12 rounded-lg border border-slate-200 bg-white p-0 dark:border-slate-600 dark:bg-slate-800"
              />
              <input
                v-model="formData.color"
                :placeholder="$t('BUSINESS_DATA.CATEGORY.COLOR_PLACEHOLDER')"
                pattern="^#[0-9A-Fa-f]{6}$"
                required
                class="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-hichat-500 focus:outline-none focus:ring-2 focus:ring-hichat-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('BUSINESS_DATA.CATEGORY.COLOR_HELP') }}
            </p>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('BUSINESS_DATA.CATEGORY.PREVIEW') }}
            </label>
            <div class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/60">
              <span
                class="inline-block rounded-full px-3 py-1 text-sm font-medium"
                :style="{ backgroundColor: formData.color, color: getTextColor(formData.color) }"
              >
                {{ formData.name || $t('BUSINESS_DATA.CATEGORY.PREVIEW_PLACEHOLDER') }}
              </span>
            </div>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('BUSINESS_DATA.CATEGORY.SELECT_CATEGORY') }}
            </label>
            <div class="flex max-h-56 flex-col gap-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/60">
              <div
                v-for="cat in filteredCategories"
                :key="cat.id"
                class="flex items-center justify-between gap-2"
              >
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="inline-block rounded-full px-3 py-1 text-sm font-medium"
                    :style="{ backgroundColor: cat.color, color: getTextColor(cat.color) }"
                  >
                    {{ cat.name }}
                  </span>
                  <span class="text-xs text-slate-500 dark:text-slate-400 truncate">#{{ cat.id }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <woot-button variant="clear" size="small" @click.prevent.stop="openEditModal(cat)">
                    <fluent-icon icon="edit" size="14" />
                  </woot-button>
                  <woot-button variant="clear" size="small" color-scheme="danger" @click.prevent.stop="deleteCategory(cat)">
                    <fluent-icon icon="delete" size="14" />
                  </woot-button>
                </div>
              </div>
              <div v-if="!filteredCategories.length" class="text-sm text-slate-500 dark:text-slate-400">
                {{ $t('BUSINESS_DATA.EMPTY.TITLE') }}
              </div>
            </div>
          </div>
      </div>

      <div class="flex justify-center gap-3 pt-4">
        <woot-button
          type="button"
          variant="smooth"
          color-scheme="ghost"
          @click="onClose"
        >
          {{ $t('COMMON.CANCEL') }}
        </woot-button>

        <woot-button
          type="submit"
          variant="smooth"
          color-scheme="success"
          :is-loading="isSaving"
          :disabled="!isFormValid"
        >
          {{ $t('COMMON.CREATE') }}
        </woot-button>
      </div>
    </form>

    <CategoryEditModal
      :show="showEditModal"
      :category="editTarget"
      :category-type="formData.category_type || categoryType"
      @close="closeEditModal"
      @updated="onCategoryUpdated"
    />
  </woot-modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import CategoryEditModal from './CategoryEditModal.vue';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';

export default {
  name: 'CategoryModal',
  components: { CategoryEditModal, FormLabel },
  setup() {
    return { v$: useVuelidate() };
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    categoryType: {
      type: String,
      default: '',
    },
  },
  
  data() {
    return {
      isSaving: false,
      formData: {
        name: '',
        color: '#6B7280',
        category_type: '',
      },
      isLoadingList: false,
      categories: [],
      showEditModal: false,
      editTarget: null,
    };
  },
  
  computed: {
    isFormValid() {
      const colorPattern = /^#[0-9A-Fa-f]{6}$/;
      return this.formData.name.trim() && 
             this.formData.color &&
             colorPattern.test(this.formData.color) &&
             this.formData.category_type &&
             this.formData.name.length <= 100;
    },
    
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },

    filteredCategories() {
      const type = this.formData.category_type || this.categoryType;
      return (this.categories || []).filter(c => c.type === type);
    },
  },
  validations: {
    formData: {
      name: { required, maxLength: maxLength(100) },
      category_type: { required },
      color: { required },
    },
  },
  
  watch: {
    categoryType: {
      immediate: true,
      handler(newType) {
        if (newType) {
          this.formData.category_type = newType;
        }
      },
    },
    
    show(newShow) {
      if (!newShow) {
        this.resetForm();
        this.closeEditModal();
      } else {
        this.loadCategories();
      }
    },
  },
  
  methods: {
    resetForm() {
      this.formData = {
        name: '',
        color: '#6B7280',
        category_type: this.categoryType || '',
      };
      this.v$.$reset();
    },
    
    getTextColor(backgroundColor) {
      if (!backgroundColor) return '#000000';
      
      // Convert hex to RGB
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Calculate luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    
    async onSubmit() {
      if (!this.isFormValid || this.isSaving) {
        return;
      }
      
      this.isSaving = true;
      try {
        const payload = {
          name: this.formData.name.trim(),
          color: this.formData.color,
          category_type: this.formData.category_type,
          is_active: true,
        };
        
        const response = await window.axios.post(
          `/api/v1/accounts/${this.currentAccountId}/business_categories`,
          payload
        );
        
        this.$emit('created', response.data);
        useAlert(this.$t('BUSINESS_DATA.CATEGORY.CREATE_SUCCESS'), { type: 'success' });
        await this.loadCategories();
        this.resetForm();
        this.onClose();
      } catch (error) {
        console.error('Error creating category:', error);
        console.error('Error response:', error?.response?.data);
        
        let errorMessage = this.$t('BUSINESS_DATA.ERROR.SAVE_FAILED');
        
        if (error?.response?.data?.errors) {
          errorMessage = error.response.data.errors.join(', ');
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        useAlert(errorMessage, { type: 'error' });
      } finally {
        this.isSaving = false;
      }
    },
    
    async loadCategories() {
      if (!this.currentAccountId) return;
      this.isLoadingList = true;
      try {
        const { data } = await window.axios.get(`/api/v1/accounts/${this.currentAccountId}/business_categories`, { params: { per_page: 200 } });
        this.categories = data.business_categories || [];
      } catch (e) {
        // ignore
      } finally {
        this.isLoadingList = false;
      }
    },

    openEditModal(cat) {
      this.editTarget = { ...cat };
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editTarget = null;
    },

    async onCategoryUpdated() {
      await this.loadCategories();
      this.closeEditModal();
      this.$emit('updated');
      useAlert(this.$t('BUSINESS_DATA.CATEGORY.UPDATE_SUCCESS'), { type: 'success' });
    },

    async deleteCategory(cat) {
      if (!window.confirm(this.$t('BUSINESS_DATA.ITEMS.DELETE_CONFIRM') || 'Xoá danh mục?')) return;
      try {
        await window.axios.delete(`/api/v1/accounts/${this.currentAccountId}/business_categories/${cat.id}`);
        await this.loadCategories();
        useAlert(this.$t('BUSINESS_DATA.CATEGORY.DELETE_SUCCESS'), { type: 'success' });
      } catch (e) {
        console.error('Error deleting category:', e);
        const apiError =
          e?.response?.data?.error ||
          (Array.isArray(e?.response?.data?.errors) ? e.response.data.errors.join(', ') : null) ||
          e?.response?.data?.message;
        const message = apiError || this.$t('BUSINESS_DATA.ERROR.DELETE_FAILED');
        useAlert(message, { type: 'error' });
      }
    },

    onClose() {
      this.resetForm();
      this.closeEditModal();
      this.$emit('close');
    },
  },
  
  emits: ['close', 'created', 'updated'],
};
</script>
